<script setup>
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import * as ort from "onnxruntime-web";
import * as processing from "@/assets/modelrunner/processing";
import { query } from "vue-gtag";
import { errorMessages } from "@vue/compiler-sfc";

const props = defineProps({
  modelURL: { type: String, default: "" },
});
const route = useRoute();

// --------------------- UI State ---------------------
const loadingState = ref("");
const loadedFileName = ref("");
const executionProvider = ref("webgpu");
const loadError = ref("");
const count = ref(0);
const results = ref([]);

// Default mode is "upload"
const mode = ref("single_image");
const videoEl = ref(null);
const imgEl = ref(null);
const canvasEl = ref(null);
const fileInput = ref(null);
const isDragging = ref(false);
const isProcessingImage = ref(false);
const bottomMessage = ref("");
const cameraList = ref("");
const selectedCamera = ref("");
const hideBoxes = ref(false);

var cameraStream = null;
var isRunningLiveInference = false; // prevent overlapping inferences
var canvasCtx=null;
var labels=[];
var runnerOK=false;
var model_params={};
var classColors=[];
var model_output={};
var loadAttempts=0;                // retry counter for transient load failures
const MAX_LOAD_ATTEMPTS=3;
const LOAD_RETRY_DELAY_MS=1500;
const sidebarOpen = ref(true);
//---------------------- worker Thread ------------------
const worker = new Worker(new URL('@/assets/modelrunner/worker.js', import.meta.url));
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import HelpTip from '@/components/HelpTip.vue';

worker.onmessage = (e) => {
  if (e.data.type === 'load_status') {
    bottomMessage.value = e.data.message;
  }

  if (e.data.type === 'loaded') {
    console.log("Model loaded");
    loadAttempts = 0;
    onModelLoaded(e.data.params);
  }

  if (e.data.type === 'load_error') {
     // Transient failures (e.g. a dropped/timed-out model download) are common
     // against remote backends; retry the whole load a few times before giving up.
     if (loadAttempts < MAX_LOAD_ATTEMPTS) {
       loadAttempts += 1;
       bottomMessage.value = `Model load failed, retrying (${loadAttempts}/${MAX_LOAD_ATTEMPTS})…`;
       setTimeout(() => loadModel(), LOAD_RETRY_DELAY_MS * loadAttempts);
       return;
     }
     bottomMessage.value = (e.data.params && e.data.params.error) || 'Failed to load model';
     onModelLoaded(e.data.params);
  }
  if (e.data.type === 'run_error') {
    isProcessingImage.value=false;
    isRunningLiveInference = false;  
    bottomMessage.value=e.data.err;
    
  }
  if (e.data.type === 'result') {
    isProcessingImage.value=false;

    //Catch the last live results when changing from live to image
    if(isRunningLiveInference && mode.value=='single_image') {
      canvasCtx.clearRect(0, 0, canvasEl.value.clientWidth, canvasEl.value.clientHeight);
    } else {
      model_output=e.data.output
      OnModelResults();
    }
    isRunningLiveInference = false; 
    
  }
};

const OnModelResults = async () => {
    try {
      const output=model_output;

      // Re-sync canvas to current CSS size × DPR on every draw.
      // This keeps the canvas sharp AND correct when the sidebar is resized or
      // collapsed after the camera was started (which invalidates the initial size).
      const dpr = window.devicePixelRatio || 1;
      const cssW = canvasEl.value.clientWidth;
      const cssH = canvasEl.value.clientHeight;
      const needW = cssW * dpr;
      const needH = cssH * dpr;
      if (canvasEl.value.width !== needW || canvasEl.value.height !== needH) {
        canvasEl.value.width  = needW;
        canvasEl.value.height = needH;
        canvasCtx = canvasEl.value.getContext('2d', { willReadFrequently: true });
        canvasCtx.scale(dpr, dpr);
      }

      let detectObjects=[];
      results.value=[];

      canvasCtx.clearRect(0, 0, cssW, cssH);

      // Compute where the source media actually appears within the canvas,
      // accounting for object-fit: contain letterboxing/pillarboxing.
      // The canvas is sized to the container; the video may not fill it fully.
      const dr = getDisplayRect();

      if (output.mask) {
        let maskObject= {
          mask: output.mask,
          color: [255,255,0,170],
        }
        let masks=[maskObject];
        drawSegmentationMask(canvasCtx, masks, dr);
      }
      
      if(output.boxes) {
        if (output.boxes.length){
          for (let k=0;k<output.boxes.length;k++) {
             drawObjectMask(canvasCtx,output.boxes[k], dr);
          }

          for (let k=0;k<output.boxes.length;k++) {
            const o=output.boxes[k];
            const box=o.box;
            let c=o.class;
            let classname=labels[c];
          
            let obj= {
              rect:[
                dr.x + box[0]*dr.w,
                dr.y + box[1]*dr.h,
                dr.x + box[2]*dr.w,
                dr.y + box[3]*dr.h,
              ],
              label:classname,
              score:o.score,
              mask:o.mask,
            };
            detectObjects.push(obj);    
          }      
          drawBoxes(canvasCtx, detectObjects, dr);
        }
      }

    } catch (err) {
      console.error("Inference error:", err);
    }

};

// --------------------- Load Model ---------------------
const loadModel = async () => {
  if (!props.modelURL) return;

  loadingState.value="loading"
  loadedFileName.value = "";
  bottomMessage.value="Loading Model ..."
 
  worker.postMessage({
    type: "load",
    params: {
      url:props.modelURL,
      labels:labels,
      type:model_params.type,
      iou_threshold:0.7,
      score_threshold:0.25,
      nc:labels.length,
      execution_provider: executionProvider.value,
    }
  });
}

const onModelLoaded = async (params) => {
  model_params=params;
  if (params && params.ok){
    // Sync local labels with what the worker resolved (may include ONNX metadata fallback)
    if (params.labels && params.labels.length > 0) {
      labels = params.labels;
    }
    loadedFileName.value = route.query.model;
    if (params.execution_provider) executionProvider.value = params.execution_provider;
    loadingState.value="ok";
    runnerOK=true;
    generateColors();
  } else {
    console.error("Model load error:", params && params.error);
    runnerOK=false;
    loadError.value = (params && params.error) ? params.error : "Error loading model";
    loadingState.value="error";
  }
  bottomMessage.value=""
};

watch(
  () => props.modelURL,
  async (newVal) => {
      loadAttempts = 0;
      await getModelDetails();
      await loadModel();
  }
);
// --------------------- Camera ---------------------
const startCamera = async () => {
  if(!selectedCamera.value) return;
  console.log("Camera:",selectedCamera.value);

  try{
    bottomMessage.value="Starting Live View - Please wait ..."
    stopCamera();

    cameraStream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: selectedCamera.value } },
      audio: false,
    });

    
    videoEl.value.srcObject = cameraStream;
    await videoEl.value.play();

    // Size canvas at physical pixel resolution (CSS × DPR) for crisp rendering.
    // The context is then scaled so all drawing commands use CSS-pixel coordinates.
    const dpr = window.devicePixelRatio || 1;
    canvasEl.value.width  = canvasEl.value.clientWidth  * dpr;
    canvasEl.value.height = canvasEl.value.clientHeight * dpr;
    canvasCtx = canvasEl.value.getContext('2d', { willReadFrequently: true });
    canvasCtx.scale(dpr, dpr);
    bottomMessage.value=""
  } catch(err) {
     console.log("Cannot Start Live stream",err);
  }
  runLiveLoop();

};

const stopCamera = () => {
  if (!cameraStream) return;
  cameraStream.getTracks().forEach((t) => t.stop());
  cameraStream = null;
};

async function listCameras() {
  try {
    selectedCamera.value='';
    // Step 1 — Request temporary camera access to unlock device labels,
    // then immediately release the stream so it doesn't block startCamera.
    const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
    tempStream.getTracks().forEach(t => t.stop());

    // Step 2 — Now enumerate devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter(d => d.kind === "videoinput");

    console.log("Cameras after permission:", cameras);

    // Step 3 — Save first camera
    if (cameras.length > 0) {
      cameraList.value = cameras;
      selectedCamera.value = cameras[0].deviceId;
    }

    return cameras.length;
  } catch (err) {
    console.error("Camera access error:", err);
    return 0;
  }
}


async function toggleCamera() {
  if (cameraList.value.length<2) return;

  let index=0;
  for (let k=0;k<cameraList.value.length;k++){
    if (cameraList.value[k].deviceId==selectedCamera.value) {
      index=k;
      break;
    }
  }
  index++;
  if (index>=cameraList.value.length) index=0;
  selectedCamera.value=cameraList.value[index].deviceId;
  if(mode.value=='live') {
     startCamera();
  }
}

// --------------------- Live Loop ---------------------
const runLiveLoop = async () => {
  if (!runnerOK || !videoEl.value) return;

  const loop = async () => {
    if (mode.value!='live') return;
    if (!cameraStream) return;
    if (mode.value!='live') return;

    if (!isRunningLiveInference && videoEl.value.readyState >= 2 && videoEl.value.videoWidth > 0) {
      isRunningLiveInference = true;
      RunModel(videoEl.value);
            
    }
    requestAnimationFrame(loop);
  };

  loop();
};

// --------------------- Uploaded Image ---------------------
const handleFile = (file) => {
  if (!file) return;

  imgEl.value.src = URL.createObjectURL(file);
  imgEl.value.onload =  () => {
      const dpr = window.devicePixelRatio || 1;
      canvasEl.value.width  = canvasEl.value.clientWidth  * dpr;
      canvasEl.value.height = canvasEl.value.clientHeight * dpr;
      canvasCtx = canvasEl.value.getContext('2d', { willReadFrequently: true });
      canvasCtx.scale(dpr, dpr);
      isProcessingImage.value=true;
      setTimeout(() =>  RunModel(imgEl.value), 50);
  }
};

// --------------------- Run Model on image ---------------------
const RunModel = async (sourceEl) => {
    if (!runnerOK) return;
    try {
      model_params.labels=labels;
      count.value=count.value+1;
      //console.log("Frame",count.value);
      const data = processing.getInputFromSource(sourceEl,model_params.width,model_params.height);
      worker.postMessage({
        type: "run",
        params:model_params,
        buffer: data.buffer,
      }, [data.buffer]);
    } catch (err) {
      console.error("Inference error:", err);
    }
};


const onFileSelected = (e) => handleFile(e.target.files[0]);

// Drag/drop handlers
const onDragEnter = (e) => { e.preventDefault(); isDragging.value = true; };
const onDragLeave = (e) => { e.preventDefault(); isDragging.value = false; };
const onDragOver = (e) => e.preventDefault();
const onDrop = (e) => {
  e.preventDefault();
  isDragging.value = false;
  handleFile(e.dataTransfer.files[0]);
};

// --------------------- Watch Mode ---------------------

watch(mode, async (v) => {
  bottomMessage.value='';

  console.log("Mode",v);
  canvasCtx.clearRect(0,0,canvasEl.value.width,canvasEl.value.height);

  if (v === "live") {
    imgEl.value.src = "";
    await nextTick();
    listCameras();
    startCamera();
  } else {
    stopCamera();
  }
});

watch(selectedCamera, async (v) => {
  if(mode.value=='live') {
     startCamera();
  }
 
});

watch(hideBoxes, (v) => {
  OnModelResults();
});

//---------------------- Utility Functions -------------------

/**
 * Compute where the source media is actually displayed within the overlay canvas.
 * The canvas fills the container (100%×100%), but the media uses object-fit:contain
 * which adds letterbox/pillarbox bars. Returns { x, y, w, h } in canvas pixels.
 *
 * We compute the letterbox in CSS-pixel space (which is what object-fit:contain
 * uses), then convert to canvas-internal-pixel space using the scale factor between
 * the two. This keeps boxes correct even when the sidebar is resized after the
 * canvas dimensions were initially set.
 */
function getDisplayRect() {
  const cssW = canvasEl.value.clientWidth;  // actual CSS display size
  const cssH = canvasEl.value.clientHeight;
  if (!cssW || !cssH) return { x: 0, y: 0, w: cssW || 1, h: cssH || 1 };

  let srcW, srcH;
  if (mode.value === 'live' && videoEl.value) {
    srcW = videoEl.value.videoWidth;
    srcH = videoEl.value.videoHeight;
  } else if (imgEl.value) {
    srcW = imgEl.value.naturalWidth;
    srcH = imgEl.value.naturalHeight;
  }
  if (!srcW || !srcH) return { x: 0, y: 0, w: cssW, h: cssH };

  // Letterbox geometry in CSS-pixel space (matches object-fit:contain on the video)
  const scale = Math.min(cssW / srcW, cssH / srcH);
  const dw = srcW * scale;
  const dh = srcH * scale;
  const cssX = (cssW - dw) / 2;
  const cssY = (cssH - dh) / 2;

  // Return CSS-pixel coordinates — ctx has scale(DPR) applied, so drawing
  // at CSS px automatically hits the correct physical pixels.
  return { x: cssX, y: cssY, w: dw, h: dh };
}

function getContext(canvas) {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();

  // Increase internal resolution
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;

  const ctx = canvas.getContext("2d",{ willReadFrequently: true });
  ctx.scale(dpr, dpr);

  return ctx;
}

function labelToDarkRGBUint8(label) {
  let hash = 0;

  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) | 0;
  }

  // Keep colors dark: 0–120 range
  const r = (hash >> 16) & 0x7f;
  const g = (hash >> 8) & 0x7f;
  const b = hash & 0x7f;

  return new Uint8Array([r, g, b]);
}

function generateColors() {
  for (const label of model_params.labels){
    classColors[label] = labelToDarkRGBUint8(label);
  }
}

/**
 * Draw bounding boxes with labels outside the box.
 * @param {CanvasRenderingContext2D} ctx 
 * @param {Array} objects - [{ rect:[x1,y1,x2,y2], label, score }]
 */
function drawBoxes(ctx, objects, dr) {
  if (hideBoxes.value) return;

  // Scale text and box strokes proportionally to the video display width.
  // dr.w = 640 (model reference) → 16px font, 3px line.
  const scale     = dr ? dr.w / 640 : 1;
  const fontSize  = Math.max(9, Math.round(16 * scale));
  const lineW     = Math.max(1, 3 * scale);
  const padV      = Math.round(4 * scale);
  const padH      = Math.round(5 * scale);

  ctx.save();
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textBaseline = "bottom";

  for (const obj of objects) {
    const [x1, y1, x2, y2] = obj.rect;
    const label = obj.label;

    const [r, g, b] = classColors[label];
    const color = `rgb(${r}, ${g}, ${b})`;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineW;

    const width = x2 - x1;
    const height = y2 - y1;

    ctx.strokeRect(x1, y1, width, height);

    const text = `${label}`;
    const textWidth = ctx.measureText(text).width;
    const boxH = fontSize + padV;

    ctx.fillRect(x1 - 1, y1 - boxH, textWidth + padH * 2, boxH);
    ctx.fillStyle = "white";
    ctx.fillText(text, x1 + padH - 1, y1);
  }

  ctx.restore();
}


/**
 * Draw a single segmentation mask overlay from an ONNX tensor.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} maskTensor - { data, dims:[H,W] }
 * @param {Array} color - [r, g, b, a] (0–255)
 */
function drawSegmentationMask(ctx, maskObjects, dr) {

  let maskTensor=maskObjects[0].mask;
  const [maskHeight, maskWidth] = maskTensor.dims;
  const { x, y, w, h } = dr || getDisplayRect();

  for (const o of maskObjects) {
    let mask=o.mask.cpuData;
 
    const imageData = ctx.createImageData(maskWidth, maskHeight);
    const data = imageData.data;

    for (let i = 0; i < mask.length; i++) {
      let m=mask[i];
      if (m < model_params.nc ) {
        const label=model_params.labels[m];
        const [r, g, b] = classColors[label];

        const idx = i * 4;
        data[idx]     = r;
        data[idx + 1] = g;
        data[idx + 2] = b;
        data[idx + 3] = 190;
      }
    }

    const tmp = document.createElement("canvas");
    tmp.width = maskWidth;
    tmp.height = maskHeight;
    tmp.getContext("2d").putImageData(imageData, 0, 0);
    ctx.drawImage(tmp, x, y, w, h);
  }

}

/**
 * Draw a single segmentation mask overlay for a single object.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} maskTensor - { data, dims:[H,W] }
 * @param {Array} color - [r, g, b, a] (0–255)
 */
function drawObjectMask(ctx, obj, dr) {

  if(!obj.mask) return;

  const mask=obj.mask.data;
  const maskHeight = obj.mask.height;
  const maskWidth = obj.mask.width;
  const { x, y, w, h } = dr || getDisplayRect();

  const imageData = ctx.createImageData(maskWidth, maskHeight);
  const data = imageData.data;

  for (let py = 0; py < maskHeight; py++) 
  for (let px = 0; px < maskWidth; px++) 
  {
    const i=px+py*maskWidth;
    if(px<obj.box[0]*maskWidth || py<obj.box[1]*maskHeight || px>obj.box[2]*maskWidth || py>obj.box[3]*maskHeight) continue;

    let m=mask[i];
    if (m > 0.5) {
      const label=model_params.labels?.[obj.class];
      const color = label ? classColors[label] : null;
      if (!color) continue;
      const [r, g, b] = color;

      const idx = i * 4;
      data[idx]     = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = 190;
    }
  }

  const tmp = document.createElement("canvas");
  tmp.width = maskWidth;
  tmp.height = maskHeight;
  tmp.getContext("2d").putImageData(imageData, 0, 0);
  ctx.drawImage(tmp, x, y, w, h);

}

async function getModelDetails () {
  const dataConnect = new DatastoreConnect();
  let trainer_id = parseInt(route.query.trainer);
  const result = await dataConnect.getModelInfo({ trainer_session_id: trainer_id })
    .then(async (data) => {
      if (!data.result) {
        bottomMessage.value="Failed to get model details";
      } else {
        const model = data.result;
        if (model.labels) {
          labels=model.labels;
        }
        if (model.type) {
          model_params.type=model.type;
        }
      }
    })
    .catch((error) => {
       bottomMessage.value="Failed to get model details";
    });
  
}

// --------------------- Lifecycle ---------------------
let resizeObserver = null;

onMounted(async () => {
  canvasCtx=getContext(canvasEl.value);

  // Redraw the last inference result whenever the viewer is resized
  // (e.g. panel collapse/expand shifts the canvas CSS size).
  resizeObserver = new ResizeObserver(() => {
    if (model_output && (model_output.boxes || model_output.mask)) {
      OnModelResults();
    }
  });
  resizeObserver.observe(canvasEl.value);

  if (props.modelURL) {
      await getModelDetails();
      await loadModel();
    }
 
  if (mode.value === "live" && len>0){
    listCameras();
    startCamera();
  }

});

onBeforeUnmount(() => {
  stopCamera();
  worker.terminate();
  if (resizeObserver) resizeObserver.disconnect();
});
</script>

<template>
  <div class="runner-layout">

    <!-- ═══ TOP PANEL ═══ -->
    <header :class="['top-panel', { collapsed: !sidebarOpen }]">

      <div v-show="sidebarOpen" class="panel-content">

        <!-- Section 1: Model -->
        <div class="panel-row">
          <span class="pb-label">Model:</span>
          <span :class="['pb-value', 'model-name', { 'model-ok': loadingState === 'ok', 'model-err': loadingState === 'error' }]">{{ loadedFileName || '—' }}</span>
          <span v-if="loadingState === 'loading'" class="pb-badge loading">Loading…</span>
          <span v-if="loadingState === 'error'" class="pb-badge error" :title="loadError">Error</span>
          <span v-if="bottomMessage" class="pb-status info">{{ bottomMessage }}</span>
        </div>

        <div class="panel-divider"></div>

        <!-- Section 2: Mode + Source -->
        <div class="panel-row">
          <span class="pb-label">Mode:</span>
          <label class="pb-radio"><input type="radio" value="live" v-model="mode" :disabled="loadingState !== 'ok'" /> Live</label>
          <label class="pb-radio"><input type="radio" value="single_image" v-model="mode" :disabled="loadingState !== 'ok'" /> Upload</label>
        </div>
        <div class="panel-row">
          <template v-if="mode === 'live'">
            <span class="pb-label">Camera:</span>
            <select class="pb-select" v-model="selectedCamera" @change="startCamera" :disabled="loadingState !== 'ok'">
              <option disabled value="">— select —</option>
              <option v-for="cam in cameraList" :key="cam.deviceId" :value="cam.deviceId">
                {{ cam.label || 'Camera' }}
              </option>
            </select>
          </template>
          <template v-else>
            <button class="pb-btn" @click="fileInput.click()" :disabled="loadingState !== 'ok'">Select Image</button>
          </template>
          <div v-if="isProcessingImage" class="pb-spinner"></div>
        </div>

        <div class="panel-divider"></div>

        <!-- Section 3: Backend -->
        <div class="panel-row">
          <span class="pb-label">Backend:</span>
          <HelpTip class="backend-help-tip">If the model produces many false detections, try a different backend — some GPUs produce incorrect outputs with WebGL/WebGPU.</HelpTip>
        </div>
        <div class="panel-row backend-options">
          <label class="pb-radio pb-radio-block"><input type="radio" value="webgpu" v-model="executionProvider" @change="loadModel" :disabled="loadingState !== 'ok'" /> WebGPU <span class="pb-hint">(GPU)</span></label>
          <label class="pb-radio pb-radio-block"><input type="radio" value="webgl" v-model="executionProvider" @change="loadModel" :disabled="loadingState !== 'ok'" /> WebGL <span class="pb-hint">(OpenGL ES Shaders)</span></label>
          <label class="pb-radio pb-radio-block"><input type="radio" value="wasm" v-model="executionProvider" @change="loadModel" :disabled="loadingState !== 'ok'" /> WASM <span class="pb-hint">(CPU – slower)</span></label>
        </div>

        <div class="panel-divider"></div>

        <!-- Section 4: Hide boxes + Frame -->
        <div class="panel-row">
          <label class="pb-checkbox">
            <input type="checkbox" v-model="hideBoxes" :disabled="loadingState !== 'ok'" />
            Hide Detection Boxes
          </label>
          <span class="pb-pipe">|</span>
          <span class="pb-count">Frame {{ count }}</span>
        </div>

      </div><!-- /panel-content -->

      <!-- Centred bottom-edge chevron toggle -->
      <div class="panel-toggle-wrap">
        <button class="panel-toggle" @click="sidebarOpen = !sidebarOpen"
                :title="sidebarOpen ? 'Collapse panel' : 'Expand panel'">
          <svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path v-if="sidebarOpen" d="M1 7L7 1L13 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path v-else d="M1 1L7 7L13 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

    </header>

    <!-- ═══ MAIN VIEWER ═══ -->
    <main class="viewer-area">
      <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="onFileSelected" />
      <video v-if="mode === 'live'" ref="videoEl" class="media-el"></video>
      <img v-if="mode === 'single_image'" ref="imgEl" class="media-el" />
      <canvas ref="canvasEl" class="overlay"></canvas>
    </main>

  </div>
</template>

<style scoped>
/* ── Layout shell ── */
.runner-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  background: #fff;
  overflow: hidden;
}

/* ── Top Panel ── */
.top-panel {
  flex-shrink: 0;
  background: #DEE2E6;
  border-bottom: 1px solid #ADB5BD;
  position: relative;
}
.top-panel.collapsed {
  background: transparent;
  border-bottom: none;
}

.panel-content {
  padding: 6px 16px 4px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.panel-divider {
  height: 1px;
  background: #ADB5BD;
  margin: 3px 0;
}

.backend-options {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding-left: 8px;
}

.pb-radio-block {
  width: 100%;
}

.panel-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 24px;
}

.panel-status-row {
  padding-top: 2px;
  padding-bottom: 2px;
}

.panel-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
}

.panel-sep {
  width: 1px;
  height: 16px;
  background: #ADB5BD;
  flex-shrink: 0;
}

.pb-label {
  font-size: 11px;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.pb-value { font-size: 12px; color: #212529; }
.model-name {
  color: #1A0A5E;
  font-size: 12px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 4px;
  transition: background 0.3s, color 0.3s;
}
.model-name.model-ok  { background: #D1FAE5; color: #065F46; }
.model-name.model-err { background: #FEE2E2; color: #991B1B; }

.pb-badge { font-size: 10px; padding: 1px 5px; border-radius: 3px; font-weight: 600; }
.pb-badge.loading { background: #FFC107; color: #212529; }
.pb-badge.error   { background: #DC3545; color: #fff; }

.pb-radio {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  cursor: pointer;
  color: #212529;
  white-space: nowrap;
}
.pb-radio input[type="radio"] { accent-color: #250E81; }
.pb-hint { color: #6C757D; font-size: 11px; }
.backend-help-tip { margin-left: 4px; display: flex; align-items: center; }

.pb-checkbox {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  cursor: pointer;
  color: #212529;
  white-space: nowrap;
}
.pb-checkbox input[type="checkbox"] { accent-color: #250E81; }

.pb-select {
  background: #fff;
  border: 1px solid #CED4DA;
  color: #212529;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
}
.pb-select:focus { outline: none; border-color: #250E81; }

.pb-btn {
  background: #250E81;
  border: none;
  color: #fff;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.pb-btn:hover { background: #3d1ab3; }
.pb-btn:disabled,
input[type="radio"]:disabled,
input[type="checkbox"]:disabled,
.pb-select:disabled { opacity: 0.4; cursor: not-allowed; }
label:has(input:disabled) { opacity: 0.4; cursor: not-allowed; }

.pb-count { font-size: 11px; color: #6C757D; }

.pb-pipe { color: #ADB5BD; font-size: 13px; }

.pb-status { font-size: 11px; padding: 2px 8px; border-radius: 3px; }
.pb-status.info  { background: #CFF4FC; color: #055160; }
.pb-status.error { background: #F8D7DA; color: #842029; }

.pb-spinner {
  width: 14px; height: 14px;
  border: 2px solid #ADB5BD;
  border-top-color: #250E81;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ── Centred bottom-edge chevron toggle ── */
.panel-toggle-wrap {
  display: flex;
  justify-content: center;
}

.panel-toggle {
  background: #DEE2E6;
  border: 1px solid #ADB5BD;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 2px 22px 5px;
  cursor: pointer;
  color: #6C757D;
  line-height: 1;
  transform: translateY(1px); /* overlap 1px with panel border */
}
.panel-toggle:hover { background: #CED4DA; color: #212529; }
.top-panel.collapsed .panel-toggle {
  border-top: 1px solid #ADB5BD;
  border-radius: 0 0 8px 8px;
  background: #DEE2E6;
  transform: translateY(0);
}

/* ── Viewer ── */
.viewer-area {
  position: relative;
  flex: 1;
  overflow: hidden;
  background: #fff;
}

.media-el {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  object-fit: contain;
}

.overlay {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none;
}

@keyframes spin { to { transform: rotate(360deg); } }
</style>
